import { useState } from 'react';
import { Save } from 'lucide-react';

import { PromptIn, promptInNoteOneMax, promptInSystemTemplateMax, promptInUserTemplateMax } from '@/api/generated/schemas';
import { useSavePrompt } from '@/api/queries';
import type { PromptVersionDetail, TemplateVariable } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { FieldError, SaveBlockedNote, fieldA11y } from '@/ui/FieldError';
import { FieldLabel } from '@/ui/FieldLabel';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';
import { ActionButton, buttonState } from '@/ui/ActionButton';

type Template = 'system' | 'user';

interface TemplateTab {
  key: Template;
  label: string;
  path: 'system_template' | 'user_template';
  max: number;
}

const TEMPLATES: Record<Template, TemplateTab> = {
  system: { key: 'system', label: 'System prompt', path: 'system_template', max: promptInSystemTemplateMax },
  user: { key: 'user', label: 'User message', path: 'user_template', max: promptInUserTemplateMax },
};
const TEMPLATE_TABS = [TEMPLATES.system, TEMPLATES.user];

function Variables({ variables }: { variables: TemplateVariable[] }) {
  return (
    <dl className="prompt-variables">
      {variables.map((v) => (
        <div key={v.name}>
          <dt className="mono">{`{{ ${v.name} }}`}</dt>
          <dd className="hint">{v.description}</dd>
        </div>
      ))}
    </dl>
  );
}

function TemplateTabs({ tab, edited, failed, onTab }: { tab: Template; edited: Record<Template, boolean>; failed: Record<Template, boolean>; onTab: (tab: Template) => void }) {
  return (
    <div className="tabs prompt-tabs" role="tablist" aria-label="Templates">
      {TEMPLATE_TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          id={`edit-tab-${t.key}`}
          aria-selected={tab === t.key}
          aria-controls="edit-panel"
          className={`tab${tab === t.key ? ' is-active' : ''}`}
          onClick={() => onTab(t.key)}
        >
          {t.label}
          {edited[t.key] && <span className="badge badge-neutral">Edited</span>}
          {failed[t.key] && <span className="badge badge-danger">Needs a fix</span>}
        </button>
      ))}
    </div>
  );
}

export function PromptEditDialog({ current, onClose }: { current: PromptVersionDetail; onClose: () => void }) {
  const save = useSavePrompt();
  const [system, setSystem] = useState(current.system_template);
  const [user, setUser] = useState(current.user_template);
  const [note, setNote] = useState('');
  const [tab, setTab] = useState<Template>('system');
  const edited = { system: system !== current.system_template, user: user !== current.user_template };
  const changed = edited.system || edited.user;
  const body = { system_template: system, user_template: user, note: note.trim() || null };
  const client = fieldErrors(PromptIn, body);
  const invalid = Object.keys(client).length > 0;
  // A template the API would refuse (empty, say) shows why beside it; the server's 422 adds what only it knows.
  const errors = { ...serverFieldErrors(save.error), ...client };
  const shownTab = TEMPLATES[tab];

  return (
    <Modal
      title="Edit the research prompt"
      subtitle={`Saving makes v${current.version + 1}; research uses it from its next run.`}
      size="wide"
      onClose={onClose}
      onSubmit={() => {
        if (!changed || invalid) return;
        save.mutate(body, { onSuccess: onClose });
      }}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <ActionButton
            state={buttonState(save)}
            icon={<Save aria-hidden="true" />}
            label={`Save as v${current.version + 1}`}
            disabled={!changed || invalid}
            describedBy={invalid ? 'prompt-blocked' : undefined}
          />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={bannerError(save.error, [shownTab.path, 'note'])} />
        <TemplateTabs tab={tab} edited={edited} failed={{ system: Boolean(errors.system_template), user: Boolean(errors.user_template) }} onTab={setTab} />
        <div id="edit-panel" role="tabpanel" aria-labelledby={`edit-tab-${tab}`} className="field">
          <textarea
            key={tab}
            id="edit-template"
            {...fieldA11y('edit-template', errors[shownTab.path])}
            aria-label={shownTab.label}
            className="mono prompt-editor"
            rows={18}
            maxLength={shownTab.max}
            value={tab === 'system' ? system : user}
            spellCheck={false}
            onChange={(e) => (tab === 'system' ? setSystem : setUser)(e.target.value)}
          />
          <FieldError inputId="edit-template" message={errors[shownTab.path]} />
          <Variables variables={tab === 'system' ? current.system_variables : current.user_variables} />
        </div>
        <div className="field">
          <FieldLabel htmlFor="prompt-note" id="prompt-note-hint" label="What changed" hint="Shown in the version list. The answer schema follows the dimensions and is not edited here." />
          <input
            id="prompt-note"
            type="text"
            maxLength={promptInNoteOneMax}
            placeholder="Prompt edited"
            {...fieldA11y('prompt-note', errors.note, 'prompt-note-hint')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <FieldError inputId="prompt-note" message={errors.note} />
        </div>
        <SaveBlockedNote id="prompt-blocked" show={invalid} />
      </div>
    </Modal>
  );
}
